/**
 * Command action implementations — the executable half of the canonical
 * command system (docs/architecture/command-preset-system.md).
 *
 * Every canonical command with kind 'action' names one of these handlers
 * via actionId. CommandRegistrar registers the FULL canonical table into
 * the CommandManager (ids 'cmd.<canonicalId>') so every command is
 * configurable via shortcuts and appears in the command palette; generic
 * registration routes through dispatchCommand, and the handlers below
 * provide real behavior for everything that maps onto existing app
 * capabilities.
 *
 * Commands whose engine capability does not exist yet are 'unbound' in the
 * canonical table: they resolve by name and report honestly via notify —
 * the gap is tracked as a feature backlog, never a naming gap.
 */

import { useAppStore } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
// Direct import: the snapping barrel also exports the drei-based SnapIndicator,
// which would pin drei/fiber into this eagerly-loaded module's chunk.
import { useSnapStore } from '../../tools/snapping/SnapManager';
import { useDocumentStore } from '../../state/useDocumentStore';
import { useBlockStore } from '../../state/commandAuxStores';
import { notify } from '../../utils/notify';
import { CANONICAL_BY_ID, type CanonicalCommand } from './canonicalCommands';
import { joinSelected, explodeSelected, eraseSelected } from '../../tools/cad/ModifyTools';
import { getRegisteredAction } from './runnerRegistry';
import { useDynamicInputStore } from '../../state/useDynamicInputStore';
import { openYapSettings } from '../../settings/openYapSettings';
import { usePointStyleDialogStore } from '../../components/cad/PointStyleDialog';
import { useDraftingSettingsStore } from '../../state/useDraftingSettingsStore';
import i18n from '../../i18n';

export type CommandActionMap = Record<string, (args?: string[]) => string | void>;

/**
 * Run one canonical command by id. Shared by CommandRegistrar (generic
 * `cmd.<id>` registrations in the CommandManager) and the preset layer of
 * the CommandDispatcher. Returns a user-facing result string.
 */
export function runCanonicalCommand(id: string, args: string[] = []): { success: boolean; message: string } {
    const cmd = CANONICAL_BY_ID.get(id);
    if (!cmd) return { success: false, message: `Unknown command: ${id}` };
    // Every canonical execution source (ribbon buttons, dispatcher presets,
    // CommandRegistrar cmd.<id> actions) lands in the shared recent list —
    // the Repeat menu reflects ribbon runs too (Faz 4.7).
    if (cmd.kind !== 'unbound') {
        useDynamicInputStore.getState().pushRecentCommand(cmd.names['2d'][0] ?? cmd.id);
    }
    switch (cmd.kind) {
        case 'activateTool':
            useAppStore.getState().setActiveTool(cmd.tool as never);
            return { success: true, message: `${cmd.label}: tool activated.` };
        case 'action': {
            const handler = (cmd.actionId ? COMMAND_ACTION_HANDLERS[cmd.actionId] : undefined)
                ?? (cmd.actionId ? getRegisteredAction(cmd.actionId) : undefined);
            if (!handler) {
                notify(i18n.t('tools.notify.cmdPending', { label: cmd.label }), 'warning');
                return { success: false, message: `${cmd.label}: not implemented yet.` };
            }
            const msg = handler(args);
            return { success: true, message: msg ?? `${cmd.label} executed.` };
        }
        case 'unbound':
            notify(i18n.t('tools.notify.cmdNotImplemented', { label: cmd.label }), 'info');
            return { success: false, message: `${cmd.label}: not implemented in this build.` };
        default:
            return { success: false, message: `${cmd.label}: handled by the command parser.` };
    }
}

/** All canonical commands with a callable primary name (for palettes/menus). */
export function listCanonicalCommands(): CanonicalCommand[] {
    return Array.from(CANONICAL_BY_ID.values());
}

/** Handlers keyed by canonical actionId. */
export const COMMAND_ACTION_HANDLERS: CommandActionMap = {
    // ---------------- history ----------------
    'edit.undo': () => {
        const bim = useBIMStore.getState();
        if (bim.canUndo()) bim.undo();
        else useAppStore.getState().undo();
    },
    'edit.redo': () => {
        const bim = useBIMStore.getState();
        if (bim.canRedo()) bim.redo();
        else useAppStore.getState().redo();
    },

    // ---------------- selection ----------------
    'sel.all': () => {
        const { entities, selectedIds, addToSelection } = useAppStore.getState();
        const ledger = Object.values(useDocumentStore.getState().sceneObjects);
        const ids = new Set<string>(selectedIds);
        Object.keys(entities).forEach((id) => ids.add(id));
        ledger.forEach((o) => ids.add(o.id));
        addToSelection(Array.from(ids));
    },
    'sel.none': () => { useAppStore.getState().clearSelection(); },
    'sel.similar': () => {
        const { entities, selectedIds, select } = useAppStore.getState();
        if (selectedIds.length === 0) { notify(i18n.t('tools.notify.nothingSelected'), 'warning'); return; }
        const counts = new Map<string, number>();
        selectedIds.forEach((id) => {
            const t = entities[id]?.type;
            if (t) counts.set(t, (counts.get(t) ?? 0) + 1);
        });
        const dominant = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
        if (!dominant) { notify(i18n.t('tools.notify.selectedObjectsHaveNoType'), 'info'); return; }
        const sameType = Object.values(entities).filter((e) => e.type === dominant).map((e) => e.id);
        select(sameType);
        notify(i18n.t('tools.notify.similarSelected', { count: sameType.length }), 'success');
    },

    // ---------------- CAD modify (command-line tools) ----------------
    'mod.join': () => joinSelected(),
    'mod.explode': () => explodeSelected(),
    'mod.erase': () => eraseSelected(),
    'sel.curves': () => {
        const ledger = Object.values(useDocumentStore.getState().sceneObjects);
        const kinds = new Set(['line', 'polyline', 'circle', 'arc', 'ellipse', 'curve']);
        const ids = ledger.filter((o) => kinds.has(o.kind)).map((o) => o.id);
        if (ids.length === 0) { notify(i18n.t('tools.notify.noCurveObjectsInTheDocument'), 'info'); return; }
        useAppStore.getState().select(ids);
        notify(i18n.t('tools.notify.curvesSelected', { count: ids.length }), 'success');
    },
    'sel.points': () => {
        const ledger = Object.values(useDocumentStore.getState().sceneObjects);
        const ids = ledger.filter((o) => o.kind === 'point').map((o) => o.id);
        if (ids.length === 0) { notify(i18n.t('tools.notify.noPointObjectsInTheDocument'), 'info'); return; }
        useAppStore.getState().select(ids);
        notify(i18n.t('tools.notify.pointsSelected', { count: ids.length }), 'success');
    },
    'sel.isolate': () => useAppStore.getState().cycleHideIsolate('isolate'),
    'sel.unisolate': () => useAppStore.getState().resetTemporaryHideIsolate(),
    'sel.hide': () => {
        const ids = useAppStore.getState().selectedIds;
        if (ids.length === 0) { notify(i18n.t('tools.notify.nothingSelectedToHide'), 'info'); return; }
        useAppStore.getState().hideElementsInView(ids);
    },
    'sel.show': () => useAppStore.getState().resetTemporaryHideIsolate(),
    'sel.lock': () => {
        const ids = useAppStore.getState().selectedIds;
        if (ids.length === 0) { notify(i18n.t('tools.notify.nothingSelectedToLock'), 'info'); return; }
        const { patchSceneObject } = useDocumentStore.getState();
        ids.forEach((id) => patchSceneObject(id, { locked: true }));
    },
    'sel.unlock': () => {
        const ledger = useDocumentStore.getState().sceneObjects;
        Object.values(ledger).forEach((o) => {
            if (o.locked) useDocumentStore.getState().patchSceneObject(o.id, { locked: false });
        });
    },

    // ---------------- layers ----------------
    'lay.showAll': () => {
        const app = useAppStore.getState();
        Object.values(app.layers).forEach((l) => {
            if (!l.visible) app.updateLayer(l.id, { visible: true });
        });
    },

    // ---------------- views ----------------
    'view.top': () => { window.dispatchEvent(new CustomEvent('yap:set-view', { detail: { view: 'top' } })); },
    'view.front': () => { window.dispatchEvent(new CustomEvent('yap:set-view', { detail: { view: 'front' } })); },
    'view.right': () => { window.dispatchEvent(new CustomEvent('yap:set-view', { detail: { view: 'right' } })); },
    'view.perspective': () => { window.dispatchEvent(new CustomEvent('yap:set-view', { detail: { view: 'perspective' } })); },
    'view.zoomExtents': () => { window.dispatchEvent(new CustomEvent('yap:zoom-to-fit')); },
    'view.zoomSelection': () => { window.dispatchEvent(new CustomEvent('yap:zoom-to-selection')); },
    'view.zoomOut': () => { window.dispatchEvent(new CustomEvent('yap:zoom-out')); },
    'view.cubeToggle': () => {
        const app = useAppStore.getState();
        app.updateViewCubeOptions({ showViewCube: !app.viewCubeOptions.showViewCube });
    },
    'view.cycleDisplayMode': () => {
        const order = ['wireframe', 'shaded', 'rendered', 'xray', 'ghosted', 'hiddenLine'] as const;
        const app = useAppStore.getState();
        const idx = order.indexOf(app.view.viewMode as (typeof order)[number]);
        app.updateView({ viewMode: order[(idx + 1) % order.length] });
    },
    'view.displayWireframe': () => { useAppStore.getState().updateView({ viewMode: 'wireframe' }); },
    'view.displayShaded': () => { useAppStore.getState().updateView({ viewMode: 'shaded' }); },
    'view.displayRendered': () => { useAppStore.getState().updateView({ viewMode: 'rendered' }); },
    'view.displayGhosted': () => { useAppStore.getState().updateView({ viewMode: 'ghosted' }); },
    'view.displayXray': () => { useAppStore.getState().updateView({ viewMode: 'xray' }); },

    // ---------------- display toggles ----------------
    'grid.toggle': () => {
        useAppStore.getState().updateView({ gridVisible: !useAppStore.getState().view.gridVisible });
    },
    'axes.toggle': () => {
        useAppStore.getState().updateView({ axesVisible: !useAppStore.getState().view.axesVisible });
    },
    'system.snapToggle': () => {
        const s = useSnapStore.getState();
        s.setEnabled(!s.enabled);
        notify(i18n.t('tools.notify.osnapToggled', { state: s.enabled ? i18n.t('tools.notify.stateOff') : i18n.t('tools.notify.stateOn') }), 'info');
    },
    'system.orthoToggle': () => {
        // Shared store state: flipped by F1/F8 in the CAD & 3D workbenches
        // and consumed by the drafting tools' ortho constraint.
        useAppStore.getState().toggleOrthoMode();
        const on = useAppStore.getState().orthoMode;
        notify(i18n.t('tools.notify.orthoToggled', { state: on ? i18n.t('tools.notify.stateOn') : i18n.t('tools.notify.stateOff') }), 'info');
    },
    'system.regen': () => { window.dispatchEvent(new CustomEvent('yap:regen')); },
    // OPTIONS / sys.options: open the real application settings dialog.
    // Settings-window plan: the dispatcher opens the in-app dialog by
    // default, the shared two-column window when the user opts in.
    'system.options': () => { openYapSettings(); },

    // ---------------- panels / manage dialogs ----------------
    'panel.properties': () => {
        const app = useAppStore.getState();
        if (!app.propertiesPanelOpen) app.togglePropertiesPanel();
    },
    'panel.layers': () => {
        const app = useAppStore.getState();
        if (!app.sidebarOpen) app.toggleSidebar();
    },
    'manage.units': () => useAppStore.getState().openManageDialog('units'),
    'manage.snaps': () => useAppStore.getState().openManageDialog('snaps'),
    'manage.materials': () => useAppStore.getState().openManageDialog('materials'),
    'manage.objectStyles': () => useAppStore.getState().openManageDialog('objectStyles'),
    'manage.location': () => useAppStore.getState().openManageDialog('location'),
    'manage.phasing': () => useAppStore.getState().openManageDialog('phasing'),
    'manage.designOptions': () => useAppStore.getState().openManageDialog('designOptions'),
    'manage.startingView': () => useAppStore.getState().openManageDialog('startingView'),
    'manage.macros': () => useAppStore.getState().openManageDialog('macros'),
    'manage.images': () => useAppStore.getState().openManageDialog('images'),
    'manage.links': () => useAppStore.getState().openManageDialog('manageLinks'),
    'manage.typeProperties': () => useAppStore.getState().openManageDialog('typeProperties'),
    'manage.visibilityGraphics': () => useAppStore.getState().openManageDialog('visibilityGraphics'),
    'manage.schedules': () => useAppStore.getState().openManageDialog('schedules'),
    'manage.commandPresets': () => useAppStore.getState().openManageDialog('commandPresets'),
    'dialog.help': () => {
        const app = useAppStore.getState();
        if (!app.helpOpen) app.toggleHelp();
    },
    // PTYPE (Faz 7.3): the point-style dialog over PDMODE/PDSIZE.
    'sys.ptype': () => {
        usePointStyleDialogStore.getState().setOpen(true);
    },

    // DSETTINGS (Faz 11.13): the drafting settings center (snap/grid,
    // polar, osnap, dynamic input, quick properties tabs). Bare open lands
    // on Snap & Grid; pill right-clicks jump straight to their tab.
    'tools.dsettings': () => {
        useDraftingSettingsStore.getState().openAt('snapGrid');
    },

    // ---------------- file ----------------
    // 'file.export' is handled by the sheet-runner wave (registry fallback):
    // it writes real OBJ/SVG files. A static stub here would shadow it.
    'file.purge': () => {
        // PURGE = drop everything unreferenced: empty layers plus block
        // definitions that no insert references anymore.
        const app = useAppStore.getState();
        const usedLayers = new Set(
            Object.values(useDocumentStore.getState().sceneObjects).map((o) => o.layerId)
        );
        Object.values(app.entities).forEach((e) => usedLayers.add(e.layer));
        let removed = 0;
        Object.values(app.layers).forEach((l) => {
            if (l.id !== 'layer-0' && !usedLayers.has(l.id) && !usedLayers.has(l.name)) {
                app.deleteLayer(l.id);
                removed += 1;
            }
        });
        let purgedBlocks = 0;
        const referenced = new Set(
            Object.values(app.entities)
                .map((e) => (e.data as Record<string, unknown>)?.blockName)
                .filter((n): n is string => typeof n === 'string')
        );
        Object.keys(useBlockStore.getState().defs).forEach((name) => {
            if (!referenced.has(name) && !name.startsWith('*')) {
                useBlockStore.getState().remove(name);
                purgedBlocks += 1;
            }
        });
        const total = removed + purgedBlocks;
        notify(total > 0 ? `Purged ${removed} layer(s) and ${purgedBlocks} block definition(s).` : 'Nothing to purge.', 'success');
    },
};
